import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { HoneypotField, SMSConsentBox } from "./FormUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function Header({ settings, navPages }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navStyle = {display:'block',padding:'10px 13px',color:'#444',textDecoration:'none',fontSize:'0.88rem',fontWeight:'500',whiteSpace:'nowrap',letterSpacing:'0.2px'};
  const activeStyle = {...navStyle, background:'#4a90d9', color:'#fff'};

  return (
    <header className="site-header" data-testid="site-header">
      <div className="header-top">
        <Link to="/" className="header-logo" data-testid="header-logo">
          <img src="/images/logo.png" alt="Lazy Days Home Services" />
        </Link>
        <div className="header-tagline">Beautifully Clean Homes</div>
        <div className="header-contact">
          <a href={`tel:${settings.phone}`} className="header-phone" data-testid="header-phone">{settings.phone}</a>
          <div className="social-links">
            {settings.facebook && <a href={settings.facebook} target="_blank" rel="nofollow noreferrer" className="fb-icon" data-testid="social-facebook"><i className="fa fa-facebook"></i></a>}
            {settings.google && <a href={settings.google} target="_blank" rel="nofollow noreferrer" className="g-icon" data-testid="social-google"><i className="fa fa-google-plus"></i></a>}
          </div>
        </div>
      </div>
      <nav className="nav-bar" data-testid="nav-bar">
        <div className="nav-container">
          <button className="mobile-toggle" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">&#9776;</button>
          <ul className={`nav-links ${menuOpen ? "open" : ""}`} data-testid="nav-links">
            <li className={location.pathname === "/" ? "active" : ""}><Link to="/" onClick={() => setMenuOpen(false)} style={location.pathname === "/" ? activeStyle : navStyle}>Home</Link></li>
            {navPages.map((page) => {
              const isActive = location.pathname.includes(page.url) || (page.url === "housekeeping-service-areas" && location.pathname.startsWith("/service-areas"));
              if (page.url === "housekeeping-service-areas") {
                return (
                  <li key={page.id} className={isActive ? "active" : ""}>
                    <Link to="/service-areas" onClick={() => setMenuOpen(false)} style={isActive ? activeStyle : navStyle}>Service areas {"\u25BE"}</Link>
                    <ul className="nav-dropdown">
                      {page.children && page.children.map(c => {
                        const geoSlugMap = { "arlington-service-area": "house-cleaning-arlington-tx", "fort-worth-area": "house-cleaning-fort-worth-tx", "grand-prairie-area": "house-cleaning-grand-prairie-tx", "kennedale-area": "house-cleaning-kennedale-tx", "mansfield-area": "house-cleaning-mansfield-tx" };
                        const newSlug = geoSlugMap[c.url] || c.url;
                        return <li key={c.id}><Link to={`/service-areas/${newSlug}`} onClick={() => setMenuOpen(false)}>{c.title}</Link></li>;
                      })}
                    </ul>
                  </li>
                );
              }
              return (
                <li key={page.id} className={isActive ? "active" : ""}>
                  <Link to={`/${page.url}`} onClick={() => setMenuOpen(false)} style={isActive ? activeStyle : navStyle}>{page.title}{page.children && page.children.length > 0 && " \u25BE"}</Link>
                  {page.children && page.children.length > 0 && (<ul className="nav-dropdown">{page.children.map(c => (<li key={c.id}><Link to={`/${page.url}/${c.url}`} onClick={() => setMenuOpen(false)}>{c.title}</Link></li>))}</ul>)}
                </li>
              );
            })}
            <li className={location.pathname.startsWith("/cleaning-news") ? "active" : ""}><Link to="/cleaning-news" onClick={() => setMenuOpen(false)} style={location.pathname.startsWith("/cleaning-news") ? activeStyle : navStyle}>Cleaning News</Link></li>
            <li className={location.pathname === "/faq" ? "active" : ""}><Link to="/faq" onClick={() => setMenuOpen(false)} style={location.pathname === "/faq" ? activeStyle : navStyle}>FAQ</Link></li>
            <li><a href="https://commercialservices.lazydayshome.com/" target="_blank" rel="noreferrer" style={navStyle} data-testid="nav-commercial">Commercial Services</a></li>
          </ul>
        </div>
      </nav>
      <div className="admin-sub-nav"><Link to="/admin" data-testid="admin-link-subnav">Admin</Link></div>
    </header>
  );
}

export function Footer({ navPages }) {
  return (
    <footer className="site-footer" data-testid="site-footer">
      <div className="footer-links">
        <Link to="/">Home</Link><span>|</span>
        <Link to="/cleaning-news">Cleaning News</Link><span>|</span>
        <Link to="/service-areas">Service Areas</Link><span>|</span>
        <Link to="/faq">FAQ</Link><span>|</span>
        {navPages.filter(p => p.url !== 'housekeeping-service-areas').map(p => (<React.Fragment key={p.id}><Link to={`/${p.url}`}>{p.title}</Link><span>|</span></React.Fragment>))}
        <a href="https://commercialservices.lazydayshome.com/" target="_blank" rel="noreferrer">Commercial Services</a><span>|</span>
        <Link to="/privacy-policy">Privacy Policy</Link><span>|</span>
        <Link to="/sms-policy">SMS Policy</Link><span>|</span>
        <Link to="/admin" data-testid="admin-link-footer">Admin</Link>
      </div>
      <div className="footer-credit">
        Website designed and hosted by <a href="https://web-techservices.com" target="_blank" rel="noreferrer">Web-Techservices.com</a>
      </div>
    </footer>
  );
}

export function Sidebar({ navPages }) {
  const [form, setForm] = useState({ FName: "", LName: "", Address: "", City: "", Zip: "", Phone: "", Email: "", sms_consent: false, honeypot: "" });
  const [submitted, setSubmitted] = useState(false);
  const location = useLocation();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) return;
    try {
      await axios.post(`${API}/forms/submit`, { form_type: "LeftCoupon", first_name: form.FName, last_name: form.LName, address: form.Address, city: form.City, zipcode: form.Zip, phone: form.Phone, email: form.Email, sms_consent: form.sms_consent, honeypot: "" });
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 5000);
      setForm({ FName: "", LName: "", Address: "", City: "", Zip: "", Phone: "", Email: "", sms_consent: false, honeypot: "" });
    } catch (err) { console.error(err); }
  };
  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="save-box" data-testid="save-50-box">
        <h2>Save $50*</h2>
        <h5>$20 off initial cleaning, $10 off next three</h5>
        {submitted && <div className="success-msg" data-testid="save50-success">Form submitted!</div>}
        <form onSubmit={handleSubmit} style={{ position: "relative" }}>
          <HoneypotField value={form.honeypot} onChange={e => setForm({ ...form, honeypot: e.target.value })} />
          <div className="form-group"><label>Name:</label><div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}><input placeholder="First" value={form.FName} onChange={e => setForm({ ...form, FName: e.target.value })} required data-testid="save50-first-name" /><input placeholder="Last" value={form.LName} onChange={e => setForm({ ...form, LName: e.target.value })} required data-testid="save50-last-name" /></div></div>
          <div className="form-group"><label>Address:</label><input placeholder="Address" value={form.Address} onChange={e => setForm({ ...form, Address: e.target.value })} required data-testid="save50-address" /></div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}><input placeholder="City" value={form.City} onChange={e => setForm({ ...form, City: e.target.value })} required data-testid="save50-city" /><input placeholder="Zip" value={form.Zip} onChange={e => setForm({ ...form, Zip: e.target.value })} required data-testid="save50-zip" /></div>
          <div className="form-group"><label>Phone:</label><input type="tel" placeholder="Phone" value={form.Phone} onChange={e => setForm({ ...form, Phone: e.target.value })} required data-testid="save50-phone" /></div>
          <div className="form-group"><label>Email:</label><input type="email" placeholder="Email" value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })} required data-testid="save50-email" /></div>
          <div className="disclaimer">*New accounts only.</div>
          <SMSConsentBox checked={form.sms_consent} onChange={e => setForm({ ...form, sms_consent: e.target.checked })} testId="save50-sms-consent" compact />
          <button type="submit" className="btn-submit" data-testid="save50-submit">SUBMIT NOW</button>
        </form>
      </div>
      <nav className="sidebar-nav" data-testid="sidebar-nav">
        <Link to="/" className={`sidebar-nav-item ${location.pathname === "/" ? "active" : ""}`}>Home</Link>
        {navPages.map(p => {
          if (p.url === "housekeeping-service-areas") {
            const geoSlugMap = { "arlington-service-area": "house-cleaning-arlington-tx", "fort-worth-area": "house-cleaning-fort-worth-tx", "grand-prairie-area": "house-cleaning-grand-prairie-tx", "kennedale-area": "house-cleaning-kennedale-tx", "mansfield-area": "house-cleaning-mansfield-tx" };
            return (<React.Fragment key={p.id}>
              <Link to="/service-areas" className={`sidebar-nav-item ${location.pathname.startsWith("/service-areas") ? "active" : ""}`}>Service areas</Link>
              {p.children && p.children.map(c => (<Link key={c.id} to={`/service-areas/${geoSlugMap[c.url] || c.url}`} className="sidebar-nav-child">{c.title}</Link>))}
            </React.Fragment>);
          }
          return (<React.Fragment key={p.id}><Link to={`/${p.url}`} className={`sidebar-nav-item ${location.pathname.includes(p.url) ? "active" : ""}`}>{p.title}</Link>{p.children && p.children.map(c => (<Link key={c.id} to={`/${p.url}/${c.url}`} className="sidebar-nav-child">{c.title}</Link>))}</React.Fragment>);
        })}
        <Link to="/cleaning-news" className={`sidebar-nav-item ${location.pathname.startsWith("/cleaning-news") ? "active" : ""}`}>Cleaning News</Link>
      </nav>
    </aside>
  );
}

export function ChatWidget({ reviewLink }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const messagesEndRef = React.useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ from: "bot", text: "Hi there! I'm your cleaning assistant. I'd love to help you with your home cleaning needs. May I have your name?", time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    }
  }, [open, messages.length]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;
    const userMsg = input;
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    setMessages(prev => [...prev, { from: "user", text: userMsg, time }]);
    setInput("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat/converse`, { message: userMsg, session_id: sessionId });
      setMessages(prev => [...prev, { from: "bot", text: res.data.reply, time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    } catch {
      setMessages(prev => [...prev, { from: "bot", text: "Sorry, something went wrong. Please call us at (817) 277-7387.", time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }]);
    }
    setLoading(false);
  };

  return (
    <>
      <button className="chat-fab" onClick={() => setOpen(!open)} data-testid="chat-fab">
        {open ? <i className="fa fa-times"></i> : <i className="fa fa-comments"></i>}
      </button>
      {open && (
        <div className="chat-widget" data-testid="chat-widget">
          <div className="chat-header-modern">
            <div className="chat-header-info">
              <div className="chat-avatar"><i className="fa fa-comments"></i></div>
              <div><div className="chat-header-title">AI Assistant</div><div className="chat-header-status"><span className="chat-status-dot"></span> Online - Ready to help</div></div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close"><i className="fa fa-times"></i></button>
          </div>
          <div className="chat-messages" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`chat-msg-wrap ${m.from}`}>
                <div className={`chat-msg ${m.from}`}>{m.text}</div>
                <div className="chat-msg-time">{m.time}</div>
              </div>
            ))}
            {loading && <div className="chat-msg-wrap bot"><div className="chat-msg bot"><span className="chat-typing"><span></span><span></span><span></span></span></div></div>}
            <div ref={messagesEndRef} />
          </div>
          {reviewLink && (
            <a href={reviewLink} target="_blank" rel="noreferrer" className="chat-review-banner" data-testid="chat-review-link">
              <i className="fa fa-star"></i> Love our service? Leave us a Google Review!
            </a>
          )}
          <form className="chat-input-modern" onSubmit={sendMsg}>
            <input placeholder="Type your message..." value={input} onChange={e => setInput(e.target.value)} data-testid="chat-input" />
            <button type="submit" data-testid="chat-send" disabled={loading}><i className="fa fa-paper-plane"></i></button>
          </form>
          <div className="chat-powered">Powered by Web-TechServices-AI</div>
        </div>
      )}
    </>
  );
}

export function CouponPopup({ onClose }) {
  const [form, setForm] = useState({ FName: "", LName: "", Email: "", Phone: "", Contact: "", sms_consent: false, honeypot: "" });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) { onClose(); return; }
    try {
      await axios.post(`${API}/forms/submit`, { form_type: "CouponPopup", first_name: form.FName, last_name: form.LName, email: form.Email, phone: form.Phone, contact_method: form.Contact, sms_consent: form.sms_consent, honeypot: "" });
      setSubmitted(true);
      setTimeout(onClose, 2000);
    } catch (err) { console.error(err); }
  };
  return (
    <div className="coupon-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="coupon-popup-overlay">
      <div className="coupon-modal" data-testid="coupon-popup">
        <div className="coupon-modal-header"><span>Lazy Days Home Services</span><button onClick={onClose} data-testid="coupon-close">X</button></div>
        <div className="coupon-modal-body">
          <img src="/images/logo.png" alt="Lazy Days" />
          <h3>Save on New Services</h3>
          <div className="coupon-amount"><sup>$</sup>50<span className="cents">.00</span></div>
          {submitted ? (<div className="success-msg">Thank you! Your coupon is on the way!</div>) : (
            <form onSubmit={handleSubmit} style={{ position: "relative" }}>
              <HoneypotField value={form.honeypot} onChange={e => setForm({ ...form, honeypot: e.target.value })} />
              <div className="coupon-modal-form">
                <input placeholder="First Name*" value={form.FName} onChange={e => setForm({ ...form, FName: e.target.value })} required data-testid="coupon-fname" />
                <input placeholder="Last Name*" value={form.LName} onChange={e => setForm({ ...form, LName: e.target.value })} required data-testid="coupon-lname" />
                <input placeholder="Email*" type="email" value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })} required data-testid="coupon-email" />
                <input placeholder="Phone*" type="tel" value={form.Phone} onChange={e => setForm({ ...form, Phone: e.target.value })} required data-testid="coupon-phone" />
                <input placeholder="Best contact method*" value={form.Contact} onChange={e => setForm({ ...form, Contact: e.target.value })} className="full-width" data-testid="coupon-contact" />
              </div>
              <div className="coupon-modal-disclaimer">* $20 off initial cleaning, $10 off next three. New customers only.</div>
              <div style={{ margin: "8px 16px" }}><SMSConsentBox checked={form.sms_consent} onChange={e => setForm({ ...form, sms_consent: e.target.checked })} testId="coupon-sms-consent" compact /></div>
              <button type="submit" className="coupon-modal-submit" data-testid="coupon-submit">SUBMIT NOW!</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function ExitIntentPopup({ onClose }) {
  const [form, setForm] = useState({ FName: "", LName: "", Email: "", Phone: "", Contact: "", sms_consent: false, honeypot: "" });
  const [submitted, setSubmitted] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.honeypot) { onClose(); return; }
    try {
      await axios.post(`${API}/forms/submit`, { form_type: "ExitIntent", first_name: form.FName, last_name: form.LName, email: form.Email, phone: form.Phone, contact_method: form.Contact, sms_consent: form.sms_consent, honeypot: "" });
      setSubmitted(true);
      setTimeout(onClose, 2500);
    } catch (err) { console.error(err); }
  };
  return (
    <div className="coupon-overlay" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="exit-intent-overlay">
      <div className="coupon-modal" data-testid="exit-intent-popup">
        <div className="coupon-modal-header"><span>Wait! Before You Go...</span><button onClick={onClose} data-testid="exit-intent-close">X</button></div>
        <div className="coupon-modal-body">
          <img src="/images/logo.png" alt="Lazy Days" />
          <h3>Give Us a Try!</h3>
          <div className="coupon-amount"><sup>$</sup>50<span className="cents">.00</span></div>
          <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>Save on your first cleaning service</p>
          {submitted ? (<div className="success-msg">Thank you! Your coupon is on the way!</div>) : (
            <form onSubmit={handleSubmit} style={{ position: "relative" }}>
              <HoneypotField value={form.honeypot} onChange={e => setForm({ ...form, honeypot: e.target.value })} />
              <div className="coupon-modal-form">
                <input placeholder="First Name*" value={form.FName} onChange={e => setForm({ ...form, FName: e.target.value })} required data-testid="exit-fname" />
                <input placeholder="Last Name*" value={form.LName} onChange={e => setForm({ ...form, LName: e.target.value })} required data-testid="exit-lname" />
                <input placeholder="Email*" type="email" value={form.Email} onChange={e => setForm({ ...form, Email: e.target.value })} required data-testid="exit-email" />
                <input placeholder="Phone*" type="tel" value={form.Phone} onChange={e => setForm({ ...form, Phone: e.target.value })} required data-testid="exit-phone" />
                <input placeholder="Best contact method*" value={form.Contact} onChange={e => setForm({ ...form, Contact: e.target.value })} className="full-width" data-testid="exit-contact" />
              </div>
              <div className="coupon-modal-disclaimer">* $20 off initial cleaning, $10 off next three. New customers only.</div>
              <div style={{ margin: "8px 16px" }}><SMSConsentBox checked={form.sms_consent} onChange={e => setForm({ ...form, sms_consent: e.target.checked })} testId="exit-sms-consent" compact /></div>
              <button type="submit" className="coupon-modal-submit" data-testid="exit-intent-submit">CLAIM MY DISCOUNT!</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReviewsSection({ reviews, reviewLink }) {
  if (!reviews || reviews.length === 0) return null;
  const overallRating = window.__gbpRating || 0;
  const totalReviews = window.__gbpTotal || 0;
  return (
    <section className="reviews-section" data-testid="reviews-section">
      <h1>What Our Customers Say</h1>
      <div className="divider"></div>
      {overallRating > 0 && (
        <div className="reviews-overall" data-testid="reviews-overall">
          <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="reviews-google-logo" />
          <div className="reviews-overall-stars">
            <span className="reviews-rating-num">{overallRating}</span>
            <span className="reviews-stars-display">{"★".repeat(Math.round(overallRating))}{"☆".repeat(5 - Math.round(overallRating))}</span>
          </div>
          <div className="reviews-total">Based on {totalReviews} reviews</div>
        </div>
      )}
      <div className="reviews-grid">
        {reviews.filter(r => r.rating >= 4).slice(0, 5).map((r, i) => (
          <div className="review-card" key={i} data-testid={`review-${i}`}>
            <div className="review-card-header">
              {r.profile_photo && <img src={r.profile_photo} alt={r.author_name} className="review-avatar" loading="lazy" width="40" height="40" />}
              <div>
                <div className="review-author">{r.author_name}</div>
                <div className="review-time">{r.time}</div>
              </div>
            </div>
            <div className="review-stars">{"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)}</div>
            <p className="review-text">{r.text.length > 200 ? r.text.slice(0, 200) + "..." : r.text}</p>
          </div>
        ))}
      </div>
      {reviewLink && (
        <div style={{ textAlign: "center", marginTop: 24 }}>
          <a href={reviewLink} target="_blank" rel="noreferrer" className="review-cta-btn" data-testid="leave-review-btn">
            <i className="fa fa-star"></i> Leave Us a Review on Google
          </a>
        </div>
      )}
    </section>
  );
}
